import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { IconChevronDown } from '@tabler/icons-react';

const FAQ_ITEMS = [
  {
    question: 'How do I know if I need a 5-yard or 10-yard dumpster?',
    answer:
      'A good rule of thumb: if your project fills about 2 pickup truck loads, go with the 5YD ($329.99). If closer to 4 truck loads, choose the 10YD ($379.99). For heavy debris like concrete, the 5YD is usually better due to weight limits. Unsure? Call or text us at 952-931-2577 — we\'ll figure it out with you in under a minute.',
  },
  {
    question: 'Will the dumpster fit in my driveway?',
    answer:
      'Almost certainly yes. The 5YD (8×4×4) fits in the tightest driveways and alleyways. The 10YD (10×5×5) fits in any standard parking space and most alleyways. We always place 2×4s under the bin so it won\'t scratch, crack, or damage your driveway surface. If you have a sloped or narrow driveway, just let us know when ordering and we\'ll confirm fit before delivery.',
  },
  {
    question: 'What if I have more than what fits in my first dumpster?',
    answer:
      'No worries! If your dumpster fills up, we can swap it or do a dump-and-return. We always offer a discount on your next bin, though the exact amount depends on a few factors. Just reach out so we can make sure you get the best solution for your project.',
  },
  {
    question: 'Why can concrete only go in the 5-yard?',
    answer:
      'Heavy materials like concrete, asphalt, brick, and shingles are extremely dense. A fully loaded 5YD with these materials can exceed the 3-ton weight limit. Using a 10YD could risk unsafe weight before the bin looks full. Please contact us before placing heavy materials in a 10YD.',
  },
  {
    question: 'Can I rent more than one dumpster at a time?',
    answer:
      'Yes. We offer multi-bin rentals at a discount. This is popular for larger renovation projects or commercial jobs. Call or text us at 952-931-2577 to discuss pricing and scheduling.',
  },
  {
    question: 'What are the rental periods for each size?',
    answer:
      'The standard rental period is 7 days for the 5YD and 10 days for the 10YD. Extensions are available — just reach out before your pickup date and we\'ll arrange it. Need an earlier pickup? Just call, text, or message us. We can usually get it picked up within 24 hours (except on the weekends).',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const AccordionItem = ({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 px-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-[#333333] pr-4">{question}</span>
        <IconChevronDown
          size={20}
          stroke={2}
          className={`text-[#E31837] shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="px-4 text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

export const FaqSection = () => {
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-10 italic text-center">
              Frequently Asked Questions
            </h2>

            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
              {FAQ_ITEMS.map((item) => (
                <AccordionItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
